import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { X, GraduationCap } from "lucide-react";

const formSchema = z.object({
  studentName: z.string().min(2, "Student name is required"),
  parentName: z.string().min(2, "Parent name is required"),
  classApplying: z.string().min(1, "Please select a class"),
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  location: z.string().min(2, "Location is required"),
  mobile: z.string().min(10, "Valid mobile number required"),
  email: z.string().email("Valid email is required"),
  previousSchool: z.string().optional(),
  message: z.string().optional(),
});

const classOptions = [
  "Pre-Nursery",
  "Nursery",
  "LKG",
  "UKG",
  "Class 1",
  "Class 2",
  "Class 3",
  "Class 4",
  "Class 5",
  "Class 6",
  "Class 7",
  "Class 8",
  "Class 9",
  "Class 10",
];

export const AdmissionFormDialog = ({ isOpen, onClose }) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      studentName: "",
      parentName: "",
      classApplying: "",
      dateOfBirth: "",
      location: "",
      mobile: "",
      email: "",
      previousSchool: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    try {
      // Store in localStorage for demo
      const existingEnquiries = JSON.parse(localStorage.getItem("admissionEnquiries") || "[]");
      const newEnquiry = {
        ...data,
        id: Date.now(),
        submittedAt: new Date().toISOString(),
      };
      localStorage.setItem("admissionEnquiries", JSON.stringify([...existingEnquiries, newEnquiry]));
      
      toast.success("Application submitted successfully!", {
        description: "We will contact you soon regarding the admission process.",
      });
      reset();
      onClose();
    } catch (error) {
      toast.error("Failed to submit application. Please try again.");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="admission-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <GraduationCap className="text-white" size={20} />
            </div>
            <div>
              <DialogTitle className="font-heading text-xl">Admission Enquiry</DialogTitle>
              <DialogDescription>Fill in the details to apply for admission at ID4U School</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="studentName">Student Name *</Label>
              <Input
                id="studentName"
                placeholder="Enter student name"
                {...register("studentName")}
                className={errors.studentName ? "border-red-500" : ""}
                data-testid="input-student-name"
              />
              {errors.studentName && (
                <p className="text-xs text-red-500">{errors.studentName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="parentName">Parent Name *</Label>
              <Input
                id="parentName"
                placeholder="Enter parent name"
                {...register("parentName")}
                className={errors.parentName ? "border-red-500" : ""}
                data-testid="input-parent-name"
              />
              {errors.parentName && (
                <p className="text-xs text-red-500">{errors.parentName.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="classApplying">Class Applying For *</Label>
              <Select onValueChange={(value) => setValue("classApplying", value)}>
                <SelectTrigger className={errors.classApplying ? "border-red-500" : ""} data-testid="select-class">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  {classOptions.map((cls) => (
                    <SelectItem key={cls} value={cls}>
                      {cls}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.classApplying && (
                <p className="text-xs text-red-500">{errors.classApplying.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="dateOfBirth">Date of Birth *</Label>
              <Input
                id="dateOfBirth"
                type="date"
                {...register("dateOfBirth")}
                className={errors.dateOfBirth ? "border-red-500" : ""}
                data-testid="input-dob"
              />
              {errors.dateOfBirth && (
                <p className="text-xs text-red-500">{errors.dateOfBirth.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="location">Location / City *</Label>
              <Input
                id="location"
                placeholder="Enter city"
                {...register("location")}
                className={errors.location ? "border-red-500" : ""}
                data-testid="input-location"
              />
              {errors.location && (
                <p className="text-xs text-red-500">{errors.location.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile Number *</Label>
              <Input
                id="mobile"
                type="tel"
                placeholder="Enter mobile number"
                {...register("mobile")}
                className={errors.mobile ? "border-red-500" : ""}
                data-testid="input-mobile"
              />
              {errors.mobile && (
                <p className="text-xs text-red-500">{errors.mobile.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter email address"
              {...register("email")}
              className={errors.email ? "border-red-500" : ""}
              data-testid="input-email"
            />
            {errors.email && (
              <p className="text-xs text-red-500">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="previousSchool">Previous School Name</Label>
            <Input
              id="previousSchool"
              placeholder="Enter previous school name (if any)"
              {...register("previousSchool")}
              data-testid="input-previous-school"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message / Notes</Label>
            <Textarea
              id="message"
              placeholder="Any additional information..."
              rows={3}
              {...register("message")}
              data-testid="input-message"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-secondary hover:bg-secondary/90"
              disabled={isSubmitting}
              data-testid="submit-btn"
            >
              {isSubmitting ? "Submitting..." : "Submit Application"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
